import React, { useCallback, useMemo, useState } from "react";
import { Select } from "formik-antd";
import { useTranslation } from "react-i18next";
import { Material } from "/app/src/models";
import { buildParams } from "/app/src/helpers";
import { useQuery } from "@tanstack/react-query";
import { materialService } from "/app/src/services";
import debounce from "lodash/debounce";
import { Spinner } from "@heroui/react";

/**
 * DebouncedMaterialSelect is a React functional component that renders a searchable, debounced select input for materials.
 *
 * This component uses a debounce mechanism to limit the frequency of search queries as the user types,
 * and fetches material options asynchronously. It integrates with react-query for data fetching and caching,
 * and supports internationalization via react-i18next.
 *
 * Props:
 * - `name`: The name of the field, used for form integration.
 * - `placeholder`: Placeholder text displayed in the select input.
 * - `debounceTimeout` (optional): The debounce delay in milliseconds before triggering a search (default: 300ms).
 * - `setFieldValue`: Callback function to update the selected value in the parent form.
 *
 * Features:
 * - Debounced search input to reduce API calls.
 * - Asynchronous loading and caching of material options.
 * - Customizable not-found content based on loading/error/search state.
 * - Allows clearing the selection.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.name - Field name for the select input.
 * @param {string} props.placeholder - Placeholder text for the select input.
 * @param {number} [props.debounceTimeout=300] - Debounce delay in milliseconds.
 * @param {(field: string, value: string) => void} props.setFieldValue - Callback to set the selected value.
 */
const DebouncedMaterialSelect: React.FC<{
  name: string;
  placeholder: string;
  debounceTimeout?: number;
  setFieldValue: (field: string, value: string) => void;
}> = ({ name, placeholder, debounceTimeout = 300, setFieldValue }) => {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const { t } = useTranslation();

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchTerm(value);
      }, debounceTimeout),
    [debounceTimeout],
  );

  const {
    data: materials = [],
    isFetching,
    isError,
  } = useQuery(
    ["materialsSelect", debouncedSearchTerm],
    async () => {
      if (!debouncedSearchTerm) {
        return [];
      }

      const response = await materialService.getAll(
        buildParams({
          search: debouncedSearchTerm,
          limit: 150,
          orderBy: "name",
        }),
      );
      const materials = response.materials || [];
      return materials.map((material: Material) => ({
        label: `${material.name} | In Stock: ${material.currentQuantity}`,
        value: material.name,
        key: material.name,
      }));
    },
    {
      enabled: Boolean(debouncedSearchTerm),
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  );

  // Handle search input change
  const handleSearch = useCallback(
    (value: string) => {
      debouncedSetSearchTerm(value);
    },
    [debouncedSetSearchTerm],
  );

  // Handle selection change to extract just the value string
  const handleChange = useCallback(
    (selectedValue: { label: string; value: string; key: string } | null) => {
      if (selectedValue) {
        setFieldValue(name, selectedValue.value);
      } else {
        setFieldValue(name, "");
      }
    },
    [name, setFieldValue],
  );

  const getNotFoundContent = useCallback(() => {
    if (isFetching) {
      return <Spinner size="sm" />;
    }
    if (isError) {
      return t("translation:error_loading_materials");
    }
    if (!debouncedSearchTerm) {
      return t("translation:start_typing_to_search_materials");
    }
    return t("translation:no_materials_found");
  }, [isFetching, isError, debouncedSearchTerm, t]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [],
  );

  return (
    <Select
      name={name}
      labelInValue
      showSearch
      filterOption={false}
      onSearch={handleSearch}
      onChange={handleChange}
      placeholder={placeholder}
      notFoundContent={getNotFoundContent()}
      options={materials}
      size="large"
      style={{ width: "100%" }}
      allowClear
      onInputKeyDown={handleInputKeyDown}
    />
  );
};

export default DebouncedMaterialSelect;
